import { useCallback, useContext } from 'react';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ImpersonatorContext from 'contexts/ImpersonatorContext';
import RolesContext from 'contexts/RolesContext';
import WallboardContext from 'contexts/WallboardContext';
import PERMISSIONS from '../../permissionDefinitions';

// Note: This should be used after the ContentProvider in the SplashScreen
const useV1HasAccess = () => {
  const currentUser = useContext(CurrentUserContext);
  const { isWallboard, isWallboardBuilder } = useContext(WallboardContext);
  const { currentPermissions } = useContext(RolesContext);
  const { impersonatorId } = useContext(ImpersonatorContext);

  const v1HasAccess = useCallback(
    ({
      access,
      resource,
    }: {
      access?: ResourceAccess;
      resource: Resource | Board;
    }) => {
      if (currentUser.isWallboardUser || isWallboard || isWallboardBuilder) {
        return true;
      }
      const currentUserId = impersonatorId || currentUser.id;

      if (!access) {
        return currentPermissions.includes(
          PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_ADMIN_ONLY,
        );
      }

      if (access.type === 'Public') {
        return true;
      } else if (access.type === 'Admin Only') {
        return currentPermissions.includes(
          PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_ADMIN_ONLY,
        );
      } else if (access.type === 'Private') {
        return (
          ('createdBy' in resource && resource.createdBy === currentUserId) ||
          currentPermissions.includes(
            PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_PRIVATE,
          )
        );
      } else if (access.type === 'Users' && access.userIds) {
        return (
          access.userIds.includes(currentUserId) ||
          ('createdBy' in resource && resource.createdBy === currentUserId) ||
          currentPermissions.includes(PERMISSIONS.CONTENT_ACCESS.VIEW_ALL_USERS)
        );
      } else {
        return true;
      }
    },
    [
      currentUser.isWallboardUser,
      currentUser.id,
      isWallboard,
      isWallboardBuilder,
      impersonatorId,
      currentPermissions,
    ],
  );

  return v1HasAccess;
};

export default useV1HasAccess;
